/**
 * Browser-only end-to-end encryption helpers.
 * The passphrase, derived keys and plaintext never leave the device.
 */

const PBKDF2_ITERATIONS = 300_000;

const enc = new TextEncoder();
const dec = new TextDecoder();

export function toBase64(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = "";
  for (let i = 0; i < arr.length; i++) binary += String.fromCharCode(arr[i]);
  return btoa(binary);
}

export function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

export function randomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

/** Derive an AES-KW-style wrapping key from the user's passphrase. */
export async function deriveWrappingKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey("raw", enc.encode(passphrase), "PBKDF2", false, [
    "deriveKey",
  ]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: salt as BufferSource, iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

/** Random 256-bit data key used to encrypt every conversation. */
export async function generateDataKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
}

export async function exportDataKey(key: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.exportKey("raw", key));
}

export async function importDataKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", raw as BufferSource, { name: "AES-GCM" }, true, [
    "encrypt",
    "decrypt",
  ]);
}

export async function wrapDataKey(
  dataKey: CryptoKey,
  wrappingKey: CryptoKey
): Promise<{ wrapped: string; iv: string }> {
  const iv = randomBytes(12);
  const raw = await exportDataKey(dataKey);
  const wrapped = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    wrappingKey,
    raw as BufferSource
  );
  return { wrapped: toBase64(wrapped), iv: toBase64(iv) };
}

export async function unwrapDataKey(
  wrapped: string,
  iv: string,
  wrappingKey: CryptoKey
): Promise<CryptoKey> {
  const raw = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: fromBase64(iv) as BufferSource },
    wrappingKey,
    fromBase64(wrapped) as BufferSource
  );
  return importDataKey(new Uint8Array(raw));
}

export async function encryptString(
  plaintext: string,
  dataKey: CryptoKey
): Promise<{ ciphertext: string; iv: string }> {
  const iv = randomBytes(12);
  const buf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    dataKey,
    enc.encode(plaintext)
  );
  return { ciphertext: toBase64(buf), iv: toBase64(iv) };
}

export async function decryptString(
  ciphertext: string,
  iv: string,
  dataKey: CryptoKey
): Promise<string> {
  const buf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: fromBase64(iv) as BufferSource },
    dataKey,
    fromBase64(ciphertext) as BufferSource
  );
  return dec.decode(buf);
}
